import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useMutation } from "@tanstack/react-query";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { showApiError, showSuccess } from "@/lib/toast";
import { forgotPassword } from "@/sideEffects/api";
import { useTranslation } from "react-i18next";
import { useAppNavigate } from "@/hooks/useAppNavigate";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { LanguageSelector } from "@/components/LanguageSelector";
import { ThemeToggle } from "@/components/theme/ThemeToggle";

const formSchema = z.object({
  email: z.string().email(),
});

type FormSchemaType = z.infer<typeof formSchema>;

export const ForgotPasswordPage = () => {
  const { t } = useTranslation();
  const { goToLogin } = useAppNavigate();

  const form = useForm<FormSchemaType>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
    },
  });

  const mutation = useMutation({
    mutationFn: (values: FormSchemaType) => {
      return forgotPassword(values.email);
    },
    onSuccess: (result) => {
      if (result.data === "True") {
        showSuccess(t("forgotPassword.newPasswordSent"));
        goToLogin();
      }
    },
    onError: showApiError,
  });

  function onSubmit(values: FormSchemaType) {
    mutation.mutate(values);
  }

  return (
    <div className="flex h-screen w-full items-center justify-center px-4 ">
      <Card className="mx-auto max-w-sm w-[350px]">
        <CardHeader>
          <CardTitle className="text-2xl">{t("forgotPassword.title")}</CardTitle>
          <CardDescription>{t("forgotPassword.description")}</CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("user.email")}</FormLabel>
                    <FormControl>
                      <Input placeholder={t("user.email")} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button type="submit" disabled={mutation.isPending}>
                {t("forgotPassword.button")}
              </Button>
            </form>
          </Form>
          <div className="mt-4 text-center text-sm underline border-t mt-8 pt-8 cursor-pointer" onClick={goToLogin}>
            {t("forgotPassword.goToLogin")}
          </div>
          <div className="flex justify-center mt-4 gap-2">
            <ThemeToggle />
            <LanguageSelector />
          </div>
          <div className="flex justify-center items-center mt-4">
            <img src="/logo.png" alt="Logo" className="h-[50px] object-contain mt-4" />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
